import { useState } from 'react';

interface FormState {
  name: string;
  email: string;
  phone: string;
  goal: string;
  message: string;
}

const steps = [
  {
    num: '1',
    title: 'Anfrage senden',
    text: 'Zwei Minuten. Kein Kleingedrucktes. Wir brauchen nur deinen Namen, dein Ziel und deine Kontaktdaten.',
    icon: 'ri-send-plane-line',
  },
  {
    num: '2',
    title: 'Persönliche Rückmeldung',
    text: 'Wir melden uns innerhalb von 24 Stunden – per Telefon oder Nachricht – und klären, was zu dir passt.',
    icon: 'ri-phone-line',
  },
  {
    num: '3',
    title: 'Probetraining vor Ort',
    text: 'Du kommst zu uns nach Pinneberg, trainierst kostenlos und entscheidest danach in aller Ruhe. Kein Druck.',
    icon: 'ri-map-pin-line',
  },
];

export default function OfferSection() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', goal: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setLoading(true);
    const body = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => body.append(k, v));
    try {
      await fetch('https://readdy.ai/api/form/d72lji2aegmdqoca80qg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const focusStyle = { borderColor: '#1A5C3D' };
  const blurStyle = { borderColor: '#e5e7eb' };

  return (
    <section id="angebot" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6">

        <div className="text-center max-w-2xl mx-auto mb-16">
          <span
            className="text-xs font-medium tracking-[0.2em] uppercase"
            style={{ fontFamily: 'Inter, sans-serif', color: '#1A5C3D' }}
          >
            Dein Einstieg
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold text-gray-900 mt-4 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Dein erstes Training<br />
            <em className="not-italic" style={{ color: '#1A5C3D' }}>kostet dich nichts.</em>
          </h2>
          <p
            className="text-gray-500 text-base mt-5 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            Kein Abo, keine Unterschrift, keine versteckten Bedingungen.
            Du kommst, probierst es – und entscheidest danach selbst.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {steps.map((step, i) => (
            <div key={i} className="relative">
              <div
                className="rounded-2xl p-7 h-full border"
                style={{ backgroundColor: '#F4F7F5', borderColor: 'rgba(26,92,61,0.12)' }}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 flex-shrink-0"
                  style={{ backgroundColor: '#1A5C3D' }}
                >
                  <i className={`${step.icon} text-white text-base`}></i>
                </div>
                <div
                  className="text-xs font-bold tracking-widest uppercase mb-1"
                  style={{ fontFamily: 'Inter, sans-serif', color: 'rgba(26,92,61,0.5)' }}
                >
                  Schritt {step.num}
                </div>
                <h3 className="text-gray-900 font-semibold text-base mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {step.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                  {step.text}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex absolute top-10 -right-3 z-10 w-6 h-6 items-center justify-center">
                  <i className="ri-arrow-right-s-line text-gray-300 text-xl"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          <div>
            <div
              className="rounded-2xl p-7 border-l-4 mb-6"
              style={{ backgroundColor: 'rgba(26,92,61,0.04)', borderColor: '#1A5C3D' }}
            >
              <h3 className="text-gray-900 font-semibold text-lg mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Was dich beim Probetraining erwartet
              </h3>
              <ul className="space-y-3">
                {[
                  'Persönliches Kennenlernen mit deinem Trainer',
                  'Verständliche Erklärung der EMS-Technologie',
                  'Erste sanfte Einheit – abgestimmt auf deinen Körper',
                  'Ehrliches Gespräch über deine Ziele und Möglichkeiten',
                  'Kein Verkaufsdruck. Keine Verpflichtung. Unser Versprechen.',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs" style={{ color: '#1A5C3D' }}></i>
                    </div>
                    <span className="text-gray-600 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="flex items-start gap-3 p-5 rounded-2xl border"
              style={{ backgroundColor: '#F4F7F5', borderColor: 'rgba(26,92,61,0.15)' }}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-lg" style={{ color: '#1A5C3D' }}></i>
              </div>
              <div>
                <div className="text-gray-900 font-semibold text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Unsere Garantie
                </div>
                <p className="text-gray-500 text-xs mt-1 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                  Kein Verkaufsgespräch beim Probetraining. Du entscheidest danach –
                  vollständig ohne Druck, ohne Verpflichtung, ohne Frist. Das ist unser Versprechen.
                </p>
              </div>
            </div>
          </div>

          <div id="formular" className="rounded-3xl p-8 md:p-10 border" style={{ borderColor: 'rgba(26,92,61,0.15)', backgroundColor: '#FAFCFB' }}>
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-12">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-full mb-5"
                  style={{ backgroundColor: 'rgba(26,92,61,0.1)' }}
                >
                  <i className="ri-check-line text-2xl" style={{ color: '#1A5C3D' }}></i>
                </div>
                <h3 className="text-gray-900 text-xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Perfekt. Wir melden uns.
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}>
                  Deine Anfrage ist bei uns eingegangen. Wir melden uns
                  innerhalb von 24 Stunden – persönlich, ohne Skript.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <h3 className="text-gray-900 text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Kostenloses Probetraining anfragen
                  </h3>
                  <p className="text-gray-400 text-xs mt-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                    2 Minuten · kostenlos · unverbindlich · Rückmeldung innerhalb 24 Std.
                  </p>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Dein Name *
                    </label>
                    <input
                      type="text" name="name" required value={form.name} onChange={handleChange}
                      placeholder="Vorname Nachname"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 outline-none transition-colors"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                      onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        E-Mail *
                      </label>
                      <input
                        type="email" name="email" required value={form.email} onChange={handleChange}
                        placeholder="deine@email.de"
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 outline-none transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                        onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                        onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                      />
                    </div>
                    <div>
                      <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Telefon
                      </label>
                      <input
                        type="tel" name="phone" value={form.phone} onChange={handleChange}
                        placeholder="+49 ..."
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 outline-none transition-colors"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                        onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                        onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Dein wichtigstes Ziel
                    </label>
                    <select
                      name="goal" value={form.goal} onChange={handleChange}
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm outline-none transition-colors cursor-pointer"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                      onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                    >
                      <option value="">Bitte wählen ...</option>
                      <option value="Abnehmen / Figur verbessern">Abnehmen &amp; Figur</option>
                      <option value="Kraft & Fitness aufbauen">Kraft &amp; Fitness</option>
                      <option value="Rücken- oder Beschwerden lindern">Rücken- &amp; Gelenkbeschwerden</option>
                      <option value="Gesundheit & Wohlbefinden steigern">Gesundheit &amp; Energie</option>
                      <option value="EMS kennenlernen">EMS einfach kennenlernen</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Kurze Nachricht (optional)
                    </label>
                    <textarea
                      name="message" value={form.message} onChange={handleChange}
                      rows={3} maxLength={500}
                      placeholder="Gibt es etwas, das wir vorab wissen sollten?"
                      className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-300 outline-none transition-colors resize-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      onFocus={(e) => Object.assign(e.currentTarget.style, focusStyle)}
                      onBlur={(e) => Object.assign(e.currentTarget.style, blurStyle)}
                    />
                    <div className="text-right mt-1">
                      <span className={`text-xs ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`} style={{ fontFamily: 'Inter, sans-serif' }}>
                        {charCount}/500
                      </span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="w-full text-white font-semibold text-xs md:text-sm py-4 rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap mt-2 disabled:opacity-50"
                    style={{ backgroundColor: loading || charCount > 500 ? '#999' : '#1A5C3D', fontFamily: 'Inter, sans-serif' }}
                    onMouseEnter={(e) => { if (!loading && charCount <= 500) e.currentTarget.style.backgroundColor = '#236B48'; }}
                    onMouseLeave={(e) => { if (!loading && charCount <= 500) e.currentTarget.style.backgroundColor = '#1A5C3D'; }}
                  >
                    {loading ? 'Wird gesendet …' : 'Kostenloses Probetraining sichern →'}
                  </button>
                  <p className="text-center text-gray-400 text-xs leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Kein Verkaufsdruck. Keine Verpflichtung.<br />Wir melden uns innerhalb von 24 Stunden persönlich.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
